import { useEffect, useState, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { SeoData } from "../../types/serviceDetail";
import SeoMeta from "../metaHead/SeoMeta";

const SeoLoader = () => {
  const [seoData, setSeoData] = useState<SeoData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const location = useLocation();
  const baseUrl = import.meta.env.VITE_API_URL;

  // Compute slug without storing it in state to avoid extra renders
  const finalSlug = useMemo(() => {
    const path = location.pathname.startsWith("/")
      ? location.pathname.slice(1)
      : location.pathname;
    return path || "/"; // fallback to root slug
  }, [location.pathname]);

  useEffect(() => {
    let isMounted = true; // prevent state update after unmount
    setLoading(true);
    setError(null);

    fetch(`${baseUrl}/api/page-seo/seo-details?slug=${finalSlug}`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch SEO data: ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        if (!isMounted) return;
        if (!data.metaTags) {
          setSeoData(null);
          return;
        }

        const formattedData: SeoData = {
          ...data,
          thirdPartyScripts: Array.isArray(data.thirdPartyScripts)
            ? data.thirdPartyScripts.map((script: any) => ({
                ...script,
                script_position: script.script_position === "head" ? "head" : "body",
              }))
            : [],
        };

        setSeoData(formattedData);
      })
      .catch((err) => {
        if (isMounted) {
          console.error("Error fetching SEO data:", err);
          setError(err.message || "Unknown error");
        }
      })
      .finally(() => {
        if (isMounted) setLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [baseUrl, finalSlug]);

  if (loading || error || !seoData?.metaTags) {
    return null; // Don't render anything if loading, error, or no meta tags
  }

  return <SeoMeta seoData={seoData} />;
};

export default SeoLoader;
